import { useState } from "react";
import { FiX } from "react-icons/fi";
import styled from "styled-components";

import { Button } from "../Buttons";
import FlexBox from "../FlexBox";
import { Body2, H4 } from "../Headings";
import Radio from "../Radio";
import {
  DAVYS_GREY_200,
  DAVYS_GREY_400,
  DAVYS_GREY_500,
  DAVYS_GREY_800,
  PLUM_PURPLE_100
} from "../colors";

const REPORT_REASONS = [
  { label: "Response was inaccurate", value: "response_inaccurate" },
  { label: "Response was incomplete", value: "response_incomplete" },
  { label: "Did not load at all", value: "did_not_load" },
  { label: "System error", value: "system_error" },
  { label: "Response repeated information", value: "response_repeated" },
];

const Container = styled(FlexBox)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  max-height: 98%;
  flex-direction: column;
  background: white;
  border-radius: 1rem 1rem 0 0;
  overflow: hidden;
  z-index: 10;
  animation: slide-up 0.25s ease-out;

  @keyframes slide-up {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

const Header = styled(FlexBox)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem 2rem 1.5rem;
  background-color: ${PLUM_PURPLE_100};
  flex-shrink: 0;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background: white;
  border-radius: 50%;
  cursor: pointer;
  color: ${DAVYS_GREY_800};
  flex-shrink: 0;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
`;

const Body = styled(FlexBox)`
  flex-direction: column;
  padding: 1rem 1.5rem;
  row-gap: 0.75rem;
  overflow-y: auto;
  flex: 1;

  background: white;
  border-radius: 1rem 1rem 0 0;
  margin-top: -1rem;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const ReasonRow = styled(FlexBox)`
  align-items: center;
  column-gap: 0.75rem;
  padding: 12px;
  border: 1px solid ${DAVYS_GREY_400};
  border-radius: 0.5rem;
  cursor: pointer;
  width: 100%;
  background: ${props => (props.selected ? DAVYS_GREY_200 : "white")};

  &:hover {
    background: ${DAVYS_GREY_200};
  }
`;

const ReasonLabel = styled(Body2)`
  color: ${DAVYS_GREY_800};
  flex: 1;
  cursor: pointer;
`;

const OtherTextArea = styled.textarea`
  width: 100%;
  border: 1px solid ${DAVYS_GREY_400};
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  font-family: "Quicksand";
  font-size: 0.875rem;
  line-height: 1.5rem;
  font-weight: 500;
  color: ${DAVYS_GREY_800};
  resize: none;
  outline: none;
  min-height: 5rem;
  box-sizing: border-box;

  ::placeholder {
    color: ${DAVYS_GREY_500};
  }
`;

const Footer = styled(FlexBox)`
  padding: 1rem 1.5rem;
  justify-content: center;
  border-top: 1px solid ${DAVYS_GREY_400};
  flex-shrink: 0;
`;

const SubmitButton = styled(Button)`
  min-width: 10rem;
`;

const ReportReasonModal = ({ onClose, onSubmit, isSubmitting = false }) => {
  const [selectedReason, setSelectedReason] = useState(null);
  const [otherReason, setOtherReason] = useState("");

  const canSubmit = selectedReason !== null || otherReason.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit || isSubmitting) return;
    onSubmit?.({
      reasons: selectedReason ? [selectedReason.value] : [],
      other_text: otherReason.trim() || undefined,
    });
  };

  return (
    <Container>
      <Header>
        <H4 bold>Please select your reason to report</H4>
        <CloseButton type="button" onClick={onClose} aria-label="Close">
          <FiX size={18} />
        </CloseButton>
      </Header>

      <Body>
        <Body2>
          This allows us to take the correct action on the reported content.
        </Body2>

        {REPORT_REASONS.map(reason => (
          <ReasonRow
            key={reason.value}
            selected={selectedReason?.value === reason.value}
            onClick={() => setSelectedReason(reason)}
          >
            <Radio active={selectedReason?.value === reason.value} size="1rem" />
            <ReasonLabel>{reason.label}</ReasonLabel>
          </ReasonRow>
        ))}

        <OtherTextArea
          placeholder="Type here for any other reason"
          value={otherReason}
          onChange={e => setOtherReason(e.target.value)}
          rows={2}
        />
      </Body>

      <Footer>
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </SubmitButton>
      </Footer>
    </Container>
  );
};

export default ReportReasonModal;
